import { useMemo } from 'react'
import { useTimeline } from '../../../context/useTimeline'
import type { DashboardChecklistVisualItem, DashboardVisualItem } from '../../../types/dashboardVisualItem'
import type { TimelineElement } from '../../../types/timelineElement'
import { getElementColor, splitMacroElements } from '../viewUtils'

const PALETTE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--accent)',
  'var(--info)',
  'var(--success)',
] as const

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export type SandBand = {
  taskId: string
  label: string
  colorToken: string
  remaining: number
  total: number
}

export type SandColumn = {
  goalId: string
  label: string
  colorToken: string
  totalWeight: number
  remainingWeight: number
  bands: SandBand[]
}

export type SandfallModel = {
  columns: SandColumn[]
  totalWeight: number
  drainedWeight: number
}

export type SandfallMode = 'unified' | 'flat'

const numericWeight = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null

export const checklistWeight = (item: DashboardChecklistVisualItem): number =>
  numericWeight(item.sourceChecklist?.weightage) ?? 1

export const buildSandfallModel = (
  elements: readonly TimelineElement[],
  visualItems: readonly DashboardVisualItem[],
  calculateElementProgress: (elementId: string) => number,
): SandfallModel => {
  const { goals, tasksByGoalId, subTasksByTaskId } = splitMacroElements([...elements])
  const checklistItems = visualItems.filter((item): item is DashboardChecklistVisualItem => item.kind === 'CHECKLIST')
  const checklistByParentId = new Map<string, DashboardChecklistVisualItem[]>()
  for (const checklist of checklistItems) {
    if (!checklist.parentId) continue
    const bucket = checklistByParentId.get(checklist.parentId) ?? []
    bucket.push(checklist)
    checklistByParentId.set(checklist.parentId, bucket)
  }

  const weightOfElement = (element: TimelineElement, seen = new Set<string>()): number => {
    if (seen.has(element.id)) return 0
    seen.add(element.id)
    const explicit = numericWeight((element as { weight?: unknown }).weight)
    if (explicit != null) return explicit

    const childElements = element.type === 'GOAL'
      ? tasksByGoalId.get(element.id) ?? []
      : element.type === 'TASK'
        ? subTasksByTaskId.get(element.id) ?? []
        : []
    const childTotal = childElements.reduce((sum, child) => sum + weightOfElement(child, seen), 0)
    const checklistTotal = (checklistByParentId.get(element.id) ?? []).reduce((sum, item) => sum + checklistWeight(item), 0)
    return childTotal + checklistTotal || 1
  }

  const remainingForElement = (element: TimelineElement): number => {
    const total = weightOfElement(element)
    const progress = clamp(calculateElementProgress(element.id), 0, 100)
    return total * (1 - progress / 100)
  }

  const columns: SandColumn[] = goals.map((goal, index) => {
    const goalTasks = tasksByGoalId.get(goal.id) ?? []
    const totalWeight = weightOfElement(goal)
    const remainingWeight = remainingForElement(goal)
    const bandsSource = goalTasks.length > 0 ? goalTasks : [goal]
    const bands = bandsSource.map((task, bandIndex) => {
      const total = weightOfElement(task)
      return {
        taskId: task.id,
        label: task.title,
        colorToken: getElementColor(task, index + bandIndex),
        total,
        remaining: remainingForElement(task),
      }
    })

    return {
      goalId: goal.id,
      label: goal.title,
      colorToken: getElementColor(goal, index) || PALETTE[index % PALETTE.length],
      totalWeight,
      remainingWeight,
      bands,
    }
  })

  const totalWeight = columns.reduce((sum, column) => sum + column.totalWeight, 0)
  const remainingWeight = columns.reduce((sum, column) => sum + column.remainingWeight, 0)
  return {
    columns,
    totalWeight,
    drainedWeight: Math.max(0, totalWeight - remainingWeight),
  }
}

export const collapseSandfallColumns = (columns: readonly SandColumn[]): SandColumn[] => {
  if (columns.length <= 1) return [...columns]
  const totalWeight = columns.reduce((sum, column) => sum + column.totalWeight, 0)
  const remainingWeight = columns.reduce((sum, column) => sum + column.remainingWeight, 0)
  return [{
    goalId: 'workspace',
    label: 'Workspace',
    colorToken: 'var(--warning)',
    totalWeight,
    remainingWeight,
    bands: columns.map((column) => ({
      taskId: column.goalId,
      label: column.label,
      colorToken: column.colorToken,
      total: column.totalWeight,
      remaining: column.remainingWeight,
    })),
  }]
}

export const useSandfallModel = (
  elements: readonly TimelineElement[],
  visualItems: readonly DashboardVisualItem[],
): SandfallModel => {
  const { calculateElementProgress } = useTimeline()
  return useMemo(
    () => buildSandfallModel(elements, visualItems, calculateElementProgress),
    [calculateElementProgress, elements, visualItems],
  )
}

export const columnCompletionPercent = (column: Pick<SandColumn, 'totalWeight' | 'remainingWeight'>): number => {
  if (column.totalWeight <= 0) return 100
  return clamp(((column.totalWeight - column.remainingWeight) / column.totalWeight) * 100, 0, 100)
}

export const findChecklistRootGoalId = (
  checklist: DashboardChecklistVisualItem,
  elementById: ReadonlyMap<string, TimelineElement>,
): string | null => {
  if (checklist.rootGoalId) return checklist.rootGoalId
  let cursor = checklist.parentId ? elementById.get(checklist.parentId) : undefined
  for (let guard = 0; cursor && guard < 20; guard += 1) {
    if (cursor.type === 'GOAL') return cursor.id
    cursor = cursor.parentId ? elementById.get(cursor.parentId) : undefined
  }
  return null
}
